import React, { FC, useMemo } from 'react';
import { useAppSelector } from '../../../store/configureStore';

import { getReportDetails } from '../../../store/reportDetails';
import { TABLE_ITEM_COUNT_LIMITS } from '../../../utils/constants';
import { ICertificate } from '../custom-general/custom-certificates/form';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import OverviewSheet from '../custom-general/overview-sheet';
import Conditional from '../../common/conditional';
import CustomCertificatePdf from '../custom-general/custom-certificates/pdf';
import ExecutiveSummary from '../custom-visual-inspection/executive-summary';
import PDFTablePage from '../../themed/pdf/pdfTablePage';
import CustomSection from '../custom-general/custom-section';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import GeneralDrawing from '../custom-visual-inspection/general-drawing';
import LPTReportSummary from './lpt-report/index';

import * as data from '../../../utils/data';
import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';


interface LPTReportProps {
	certificates?: ICertificate;
	projectDetails: models.Project;
	ipiReport: models.CustomLPTReport;
	coverData: models.ExxonMobilFVIPipingCoverData;
	executiveSummary: string;
	photoRecord: models.PDFImageProps[];
	drawing: models.PDFImageProps[];
	reportTitle: string;
}

const LPTReport: FC<LPTReportProps> = (props) => {
	const { inspectionTypeName } = props.projectDetails;
	const { summary, ...otherProps } = props.ipiReport;

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: props.coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: props.coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: props.coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: props.coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

	const mappedReportData = useMemo(() => {
		return otherProps.data.map((item, index) => ({ 
			id: index + 1, 
			description: item.observation, 
			...item 
		}));
	}, [otherProps.data]);

	const overview = useCoverSheetOverview();
	const reportDetails = useAppSelector(getReportDetails);

	const chunkedItems = utils.getItemsChunkedData(mappedReportData, TABLE_ITEM_COUNT_LIMITS.LPT, reportDetails.itemCountPerPage);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(inspectionTypeName)} />

			<CustomInfoSheet
				projectDetails={props.projectDetails}
				workOrderNumber={props.coverData.workOrderNumber}
				reportTitle={props.reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			<ExecutiveSummary summary={props.executiveSummary} />

			{props.certificates && <CustomCertificatePdf certificates={props.certificates} />}

			{utils.getChunkedData(mappedReportData, 10).map((chunk, index) => (
				<CustomSection key={index} sectionTitle="Summary">
					<div className="p-2">
						<PDFTablePage data={chunk} columns={data.getCustomLPTSummaryColumns()} />
					</div>
				</CustomSection>
			))}

			<GeneralDrawing generalDrawing={props.drawing} />

			<Conditional isVisible={otherProps.data.length > 0}>
				{chunkedItems.map((chunk, index) => (
					<CustomSection key={index} sectionTitle="Liquid Penetrant Test Report">
						<LPTReportSummary 
							{...otherProps} 
							data={chunk} 
							summary={summary} 
							showOverview={index === 0}
							showSummary={index === (chunkedItems.length - 1)} 
						/>
					</CustomSection>
				))}
			</Conditional>

			<Conditional isVisible={otherProps.data.length === 0}>
				<CustomSection sectionTitle="Liquid Penetrant Test Report">
					<LPTReportSummary {...otherProps} summary={summary} showOverview showSummary />
				</CustomSection>
			</Conditional>

			<CustomGeneralPhotos label="Photographic Report" photos={props.photoRecord} />
		</React.Fragment>
	);
};

export default withPDF(LPTReport);
